import type { CardDef, Composition, StageDef } from "@reagent/sim";
import { sameData, scalarMismatch } from "./compose";
import { GF_CORE, GF_REFINED } from "./stages/era1/gf";
import { GL_CORE, GL_REFINED } from "./stages/era1/gl";
import { MT_CORE, MT_REFINED } from "./stages/era1/mt";
import { PF_CORE, PF_REFINED } from "./stages/era1/pf";
import { TP_CORE, TP_REFINED } from "./stages/era1/tp";

/**
 * Pathways and the era pool (spec 0034).
 *
 * A pathway is a two-tier line: a core module and a refined module, each a
 * complete, stand-alone, validated stage with its own worked solution. A run
 * picks two pathways from the era's pool and composes its stages from their
 * modules (`compose.ts`); the pick itself is meta's.
 */

export interface PathwayTierDef {
  /** The line module: a complete, stand-alone, validated stage with its own worked solution. */
  readonly module: StageDef;
  /** Commodity tokens this tier imports. Each is the sole emitted species of a sourced inputTank in `module`. */
  readonly imports: readonly string[];
}

export interface PathwayDef {
  readonly id: string;
  readonly name: string;
  readonly core: PathwayTierDef;
  readonly refined: PathwayTierDef;
  /** Commodity tokens split off the core line (exports come from core lines only). */
  readonly exports: readonly string[];
  /**
   * Q10 substitute grade (`06` section 2.1): an exported token whose real source is a stand-in
   * (PF `Alkali`, GL `Brine`) maps to the impurity a linked importer's tank carries on top of the
   * exporter's worst case. Absent for a clean, authentic export. Spec 0037 OQ1.
   */
  readonly substituteImpurities?: Readonly<Record<string, Composition>>;
  /** Card ids added to the run's starting deck when this pathway is picked: deck cards only (signature Directives and the attachment); machines stay in each module's setupCatalogue. */
  readonly package: readonly string[];
}

export interface EraPoolDef {
  readonly eraId: string;
  readonly commodities: readonly string[];
  readonly pathways: readonly PathwayDef[];
}

const MINTED_ID = /^[ne]\d+$/;

/**
 * Throws, like `validateStage`, naming the first violation. A pool is checked
 * as a whole because its modules are composed onto one board: an id or a
 * recipe that is fine inside one module can still collide with another's.
 */
export function validatePool(pool: EraPoolDef, cards: readonly CardDef[]): EraPoolDef {
  const where = `pool ${pool.eraId}`;
  const cardsById = new Map(cards.map((c) => [c.id, c]));
  const commodities = new Set(pool.commodities);

  const pathwayIds = new Set<string>();
  for (const pathway of pool.pathways) {
    if (pathwayIds.has(pathway.id)) throw new Error(`${where}: duplicate pathway id ${pathway.id}`);
    pathwayIds.add(pathway.id);
  }

  const tiers = pool.pathways.flatMap((p) => [
    { pathway: p, tier: "core" as const, def: p.core },
    { pathway: p, tier: "refined" as const, def: p.refined },
  ]);
  const modules = tiers.map((t) => t.def.module);

  const nodeOwner = new Map<string, string>();
  const edgeOwner = new Map<string, string>();
  const recipeSeen = new Map<string, { readonly module: string; readonly recipe: unknown }>();
  const offerSeen = new Map<string, { readonly module: string; readonly costCredits: number }>();
  for (const module of modules) {
    for (const [kind, ids, owner] of [
      ["node", module.nodes.map((n) => n.id), nodeOwner],
      ["edge", module.edges.map((e) => e.id), edgeOwner],
    ] as const) {
      for (const id of ids) {
        if (MINTED_ID.test(id)) {
          throw new Error(`${where}: module ${module.id} ${kind} id ${id} collides with the engine's minted ids`);
        }
        const prior = owner.get(id);
        if (prior !== undefined) {
          throw new Error(`${where}: ${kind} id ${id} appears in both ${prior} and ${module.id}`);
        }
        owner.set(id, module.id);
      }
    }

    for (const recipe of module.recipes) {
      const prior = recipeSeen.get(recipe.id);
      if (prior && !sameData(prior.recipe, recipe)) {
        throw new Error(`${where}: recipe ${recipe.id} differs between ${prior.module} and ${module.id}`);
      }
      recipeSeen.set(recipe.id, { module: module.id, recipe });
    }

    for (const offer of module.setupCatalogue ?? []) {
      const prior = offerSeen.get(offer.cardId);
      if (prior && prior.costCredits !== offer.costCredits) {
        throw new Error(
          `${where}: ${offer.cardId} is offered at ${prior.costCredits} by ${prior.module} and ${offer.costCredits} by ${module.id}`,
        );
      }
      offerSeen.set(offer.cardId, { module: module.id, costCredits: offer.costCredits });
    }
  }

  for (const pathway of pool.pathways) {
    for (const cardId of pathway.package) {
      const card = cardsById.get(cardId);
      if (!card) throw new Error(`${where}: pathway ${pathway.id} package names a card that does not exist: ${cardId}`);
      if (card.category === "hazard") {
        throw new Error(`${where}: pathway ${pathway.id} package names a hazard card: ${cardId}`);
      }
    }

    for (const token of pathway.exports) {
      if (!commodities.has(token)) {
        throw new Error(`${where}: pathway ${pathway.id} exports ${token}, which is not a pool commodity`);
      }
      if (!pathway.core.module.recipes.some((r) => token in r.outputs)) {
        throw new Error(`${where}: pathway ${pathway.id} exports ${token}, which no recipe in ${pathway.core.module.id} outputs`);
      }
    }
  }

  for (const { pathway, tier, def } of tiers) {
    for (const token of def.imports) {
      if (!commodities.has(token)) {
        throw new Error(`${where}: pathway ${pathway.id} ${tier} imports ${token}, which is not a pool commodity`);
      }
      const isSourcedFeed = def.module.nodes.some((n) => {
        if (n.kind !== "inputTank" || n.sourcing === undefined) return false;
        const emitted = Object.keys(n.emits ?? {});
        return emitted.length === 1 && emitted[0] === token;
      });
      if (!isSourcedFeed) {
        throw new Error(
          `${where}: pathway ${pathway.id} ${tier} imports ${token}, but ${def.module.id} has no sourced inputTank emitting only it`,
        );
      }
    }
  }

  const mismatch = scalarMismatch(modules);
  if (mismatch) {
    const detail = mismatch.values.map((v) => `${v.id}=${v.value}`).join(", ");
    throw new Error(`${where}: ${mismatch.key} must be equal across every module (${detail})`);
  }

  return pool;
}

/**
 * `06` section 4.1's five tokens and section 4.2's five pathways. Imports and
 * exports are the table's, Q8 applied (GF's refined tier imports Brine); the
 * packages are `0034` OQ1's recommended default, deck cards only, since machines
 * stay in each module's `setupCatalogue`. Every module is validated where it is
 * authored (`stages/era1/*.ts`), and the pool as a whole at load (`index.ts`).
 */
export const ERA_1_POOL: EraPoolDef = {
  eraId: "era-1",
  commodities: ["Lime", "Fuel", "Brine", "Alkali", "Oxide"],
  pathways: [
    {
      id: "tp",
      name: "Tyrian Purple",
      core: { module: TP_CORE, imports: ["Brine"] },
      refined: { module: TP_REFINED, imports: ["Alkali"] },
      exports: ["Lime"],
      package: ["card-stir-the-vat", "card-lid"],
    },
    {
      id: "gf",
      name: "Greek Fire",
      core: { module: GF_CORE, imports: ["Lime"] },
      refined: { module: GF_REFINED, imports: ["Brine"] },
      exports: ["Fuel"],
      package: ["card-nitrogen-purge", "card-burst-disk"],
    },
    {
      id: "pf",
      name: "Rhodinon & unguents",
      core: { module: PF_CORE, imports: ["Fuel"] },
      refined: { module: PF_REFINED, imports: ["Oxide"] },
      exports: ["Alkali"],
      // PLACEHOLDER (spec 0037, needs playtest): natron is the real alkali (`06` 2.1), so a linked Alkali tank carries one extra Sludge on top of PF's worst case.
      substituteImpurities: { Alkali: { Sludge: 1 } },
      package: ["card-low-fire", "card-insulation"],
    },
    {
      id: "mt",
      name: "Copper & orichalcum",
      core: { module: MT_CORE, imports: ["Fuel", "Lime"] },
      refined: { module: MT_REFINED, imports: ["Fuel"] },
      exports: ["Oxide"],
      package: ["card-cooling-jacket-twin"],
    },
    {
      id: "gl",
      name: "Glass & glaze",
      core: { module: GL_CORE, imports: ["Lime", "Alkali", "Fuel"] },
      refined: { module: GL_REFINED, imports: ["Oxide"] },
      exports: ["Brine"],
      // PLACEHOLDER (spec 0037, needs playtest): salt pans are the real source (`06` 2.1), so a linked Brine tank carries one extra Sludge on top of GL's worst case.
      substituteImpurities: { Brine: { Sludge: 1 } },
      package: ["card-surge-drum"],
    },
  ],
};
